import { isEmptyObject } from "@/helpers/ObjectHelpers";
import { debounce, fieldComparator, getUserProperty, searchOverMultipleAttributes } from "@/helpers/helpers";
import { MEDIA_DEVICE_STATUSES, USER_CONNECTIONS_STATUSES } from "@/helpers/constants";
import { mapGetters } from "vuex";

const ALL_FILTER = "all";

export default {
  name: "UsersSearchMixin",
  data() {
    return {
      searchQueryText: "",
      alphabetFilters: [],
      activeAlphabetFilter: ALL_FILTER,
      SEARCH_ATTRIBUTES: ["firstName", "lastName", "jobTitle", "company"],
      filters: {
        isOnline: true,
        isAvailableForCall: true,
        isMyTable: false,
        isInContacts: false,
        isBanned: false,
        byRoom: []
      },
      loadMore: {
        loadedPages: 1
      }
    };
  },
  created() {
    this.alphabetFilters = [..."abcdefghijklmnopqrstuvwxyz", ALL_FILTER];
  },
  computed: {
    ...mapGetters({
      publicTable: "tables/publicTable",
      activeRoom: "room/activeRoom",
      rooms: "room/rooms",
      isSingleRoom: "room/isSingleRoom"
    }),
    searchQuery: {
      get() {
        return this.searchQueryText || "";
      },
      set: debounce(function(value) {
        this.searchQueryText = value;
      }, 600)
    },
    /**
     * Base users list to work with
     * @return {Object[]}
     * @private
     */
    _users() {
      // Note: define in child components
      return [];
    },
    /**
     * Filter users by alphabet filter
     * @return {Object[]}
     * @private
     */
    _alphabetFilteredUsers() {
      return this.getAlphabetUsers(this.activeAlphabetFilter);
    },
    /**
     * Apply basic user filters
     * @return {boolean}
     * @private
     */
    _filteredUsers() {
      let filteredUsers = this._alphabetFilteredUsers;

      if (this.filters.isOnline) {
        filteredUsers = filteredUsers.filter(user => user.isOnline);
      }

      if (this.filters.isBanned) {
        filteredUsers = filteredUsers.filter(user => user.isBanned);
      }

      if (this.filters.isAvailableForCall) {
        filteredUsers = filteredUsers.filter(user => {
          if (this.$store.getters["tables/isInPublicGallery"](user)) return false;
          if (user.tableUUID === this.publicTable?.uuid || !user.isOnline) return false;
          return (
            user.mediaDeviceStatus === MEDIA_DEVICE_STATUSES.ALLOW ||
            user.mediaDeviceStatus === MEDIA_DEVICE_STATUSES.BUSY
          );
        });
      }

      if (this.filters.isMyTable) {
        let tableUUID = this.$store.getters["tables/userTable"].uuid;
        filteredUsers = filteredUsers.filter(user => user.groupUUID === tableUUID || user.tableUUID === tableUUID);
      }

      if (this.filters.isInContacts) {
        let userConnectionsUUIDs = this.$store.state.users.userConnections
          .filter(connection => connection.connectionStatus === USER_CONNECTIONS_STATUSES.ACTIVE)
          .map(user => user.targetUserUUID);
        filteredUsers = filteredUsers.filter(user => userConnectionsUUIDs.includes(user.userUUID));
      }

      if (!this.isSingleRoom) {
        filteredUsers = filteredUsers.filter(user => this.filters.byRoom.includes(user.roomUUID));
      }

      return filteredUsers;
    },
    /**
     * Apply search to filtered users list
     * @return {Object[]}
     * @private
     */
    _searchedUsers() {
      return this._filteredUsers.filter(user =>
        searchOverMultipleAttributes(this.searchQuery, getUserProperty(user, this.SEARCH_ATTRIBUTES))
      );
    },
    /**
     * Final sorted list of users
     * @return {Object[]}
     * @private
     */
    _usersResult() {
      return this._searchedUsers.sort(fieldComparator(["firstName", "lastName"], getUserProperty));
    },
    _pagedResult() {
      return this._usersResult.slice(0, this._itemsPerPage * this.loadMore.loadedPages);
    },
    /**
     * Page size for load more
     * @return {number}
     * @private
     */
    _itemsPerPage() {
      return this.$store.state.event.config.defaultPaginationConfig &&
        this.$store.state.event.config.defaultPaginationConfig.users
        ? this.$store.state.event.config.defaultPaginationConfig.users
        : 50;
    },
    /**
     * Should show load more button
     * @return {boolean}
     */
    showLoadMoreButton() {
      return this._itemsPerPage * this.loadMore.loadedPages < this._usersResult.length;
    }
  },
  methods: {
    /**
     * Get alphabet filtered users
     * @param filterLetter {String}
     * @return {Object[]}
     */
    getAlphabetUsers(filterLetter) {
      if (filterLetter === ALL_FILTER) {
        return this._users;
      }
      return this._users.filter(user => {
        const userFirstName = user.profile && !isEmptyObject(user.profile) ? user.profile.firstName : user.firstName;
        return userFirstName && userFirstName.toLowerCase().startsWith(filterLetter.toLowerCase());
      });
    },
    /**
     * Load more users
     */
    loadMoreUsers() {
      this.loadMore.loadedPages += 1;
    },
    /**
     * Reset filters to default
     * @private
     */
    _setDefaultFilters(options, type) {
      this.searchQueryText = "";
      this.activeAlphabetFilter = ALL_FILTER;
      this.filters = Object.assign(
        {
          isOnline: true,
          isAvailableForCall: false,
          isMyTable: false,
          isInContacts: false,
          isBanned: false,
          byRoom: type === "CONTACTS" ? this.rooms.map(room => room.uuid) : [this.activeRoom.uuid]
        },
        options
      );
      this.loadMore.loadedPages = 1;
    }
  }
};
